import { AlertTriangle, Clock, Sparkles } from "lucide-react";
import { useQuotaStatus } from "../../hooks/useQuotaStatus";
import { formatTimeUntil } from "../../lib/utils";

interface DailyProgressIndicatorProps {
    className?: string;
}

export function DailyProgressIndicator({ className = "" }: DailyProgressIndicatorProps) {
    const { data: quotaData } = useQuotaStatus();

    if (!quotaData) return null;

    // Don't show progress indicators for bypass users
    if (quotaData.bypassQuota) return null;

    const storiesUsed = quotaData.stories.used;
    const storiesLimit = quotaData.stories.limit;
    const storiesRemaining = storiesLimit - storiesUsed;
    const percentage = (storiesUsed / storiesLimit) * 100;

    // Only show warnings when approaching/at limits (75%+)
    if (percentage < 75) return null;

    // Calculate warning message and level based on usage
    const getMessageAndLevel = () => {
        if (percentage < 90) {
            return {
                message: `You're doing great! ${storiesRemaining} more ${storiesRemaining === 1 ? "story" : "stories"}.`,
                level: "warning" as const,
                subtitle: `Your stories refresh with new adventures when quota resets! (resets ${formatTimeUntil(quotaData.resetDate)})`,
            };
        } else if (percentage < 100) {
            return {
                message: `Last story for now - make it special!`,
                level: "high-warning" as const,
                subtitle: `You'll get fresh stories to create when quota resets! (resets ${formatTimeUntil(quotaData.resetDate)})`,
            };
        } else {
            return {
                message: "More stories when quota resets!",
                level: "complete" as const,
                subtitle: `Your daily stories will reset and you can create more! (resets ${formatTimeUntil(quotaData.resetDate)})`,
            };
        }
    };

    const { message, level, subtitle } = getMessageAndLevel();

    // Get styling based on warning level
    const getStyleByLevel = () => {
        switch (level) {
            case "warning":
                return {
                    containerClass: "border-2",
                    containerStyle: {
                        backgroundColor: "hsl(var(--warning) / 0.1)",
                        borderColor: "hsl(var(--warning) / 0.2)",
                    },
                    textClass: "text-foreground",
                };
            case "high-warning":
                return {
                    containerClass: "border-2",
                    containerStyle: {
                        backgroundColor: "hsl(var(--warning) / 0.15)",
                        borderColor: "hsl(var(--warning) / 0.3)",
                    },
                    textClass: "text-foreground",
                };
            case "complete":
                return {
                    containerClass: "border-2",
                    containerStyle: {
                        backgroundColor: "hsl(var(--destructive) / 0.1)",
                        borderColor: "hsl(var(--destructive) / 0.2)",
                    },
                    textClass: "text-foreground",
                };
            default:
                throw new Error("Unknown level: " + level);
        }
    };

    const { containerClass, containerStyle, textClass } = getStyleByLevel();

    // Choose appropriate icon based on level
    const getIcon = () => {
        switch (level) {
            case "warning":
                return <Clock className="w-5 h-5 text-warning" />;
            case "high-warning":
                return <AlertTriangle className="w-5 h-5 text-warning" />;
            case "complete":
                return <Clock className="w-5 h-5 text-destructive" />;
            default:
                return <Sparkles className="w-5 h-5 text-primary" />;
        }
    };

    return (
        <div className={`rounded-xl p-4 ${containerClass} ${className}`} style={containerStyle}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center">{getIcon()}</div>
                    <div>
                        <p className={`text-sm font-medium ${textClass}`}>{message}</p>
                        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
                    </div>
                </div>
            </div>
        </div>
    );
}
